import { useEffect, useState } from 'react'
import { fetchDocumentBytes } from '@/lib/signedPreview'
import { cn } from '@/lib/cn'

// A folha de cálculo DESENHADA — separadores e tabelas, a partir do xlsx original.
//
// Saiu de `XlsxPreviewModal`, que era o único sítio que sabia fazer isto, quando o visualizador de
// documentos passou a precisar do mesmo miolo sem a janela à volta (Diogo, 2026-10-29: "devia
// abrir o xlsx no sítio, não mostrar um documento para abrir o xlsx"). Uma folha de cálculo é o
// conteúdo do documento como um PDF é o conteúdo de uma factura — mandar alguém a uma segunda
// janela para o ver é a mesma interrupção que o visualizador existe para acabar.
//
// O SheetJS (~1 MB) é carregado em diferido, por isso não entra no pacote principal, e a análise é
// feita no browser: os bytes nunca saem do cliente nem passam por um visualizador de terceiros.
// Cada folha vira uma tabela HTML a partir do ORIGINAL — sem perder nada e sem truncar.

export type XlsxSheet = { name: string; html: string }

export function useXlsxSheets(documentId: string | null) {
  const [sheets, setSheets] = useState<XlsxSheet[] | null>(null)
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!documentId) {
      setSheets(null)
      setBytes(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    setSheets(null)
    setBytes(null)
    void (async () => {
      try {
        const buf = await fetchDocumentBytes(documentId)
        if (!buf) throw new Error('Não foi possível obter o ficheiro.')
        if (!cancelled) setBytes(buf)
        const XLSX = await import('xlsx')
        const wb = XLSX.read(buf, { type: 'array', cellDates: true })
        const parsed = wb.SheetNames.map((name) => ({
          name,
          html: XLSX.utils.sheet_to_html(wb.Sheets[name]!),
        }))
        if (!cancelled) setSheets(parsed.length > 0 ? parsed : [{ name: '—', html: '' }])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [documentId])

  return { sheets, bytes, error, loading }
}

export function XlsxSheets({
  sheets,
  loading,
  error,
  className,
  tablesClassName,
}: {
  sheets: XlsxSheet[] | null
  loading: boolean
  error: string | null
  className?: string
  /** Onde a tabela scrolla. A janela usa `max-h-[70vh]`; o visualizador preenche a coluna. */
  tablesClassName?: string
}) {
  const [active, setActive] = useState(0)

  // Um ficheiro novo recomeça na primeira folha — senão o separador 3 de um ficheiro fica
  // escolhido num ficheiro que só tem uma, e a vista aparece vazia.
  useEffect(() => {
    setActive(0)
  }, [sheets])

  if (loading) return <p className="py-8 text-center text-[13px] text-gray-500">A carregar…</p>
  if (error) return <p className="py-8 text-center text-[13px] text-rose-600">{error}</p>
  if (!sheets) return null

  return (
    <div className={cn('flex min-h-0 flex-col gap-3', className)}>
      {sheets.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {sheets.map((s, i) => (
            <button
              key={s.name + i}
              type="button"
              onClick={() => setActive(i)}
              className={
                'rounded-md px-2.5 py-1 text-[12px] font-medium ' +
                (i === active
                  ? 'bg-brand-100 text-brand-800'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
              }
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div
        className={cn(
          'xlsx-preview overflow-auto rounded-md border border-DEFAULT',
          tablesClassName,
        )}
        // O SheetJS escapa o conteúdo das células em entidades HTML, por isso a tabela desenhada
        // não traz marcação executável vinda dos dados da folha.
        dangerouslySetInnerHTML={{ __html: sheets[active]?.html ?? '' }}
      />
    </div>
  )
}
